import React, { ReactNode, useState } from "react";
import { createUseStyles } from "react-jss";
import { shallowEqual, useSelector } from "react-redux";
import {
  EuiAccordion,
  EuiBasicTableColumn,
  EuiContextMenuItem,
  EuiContextMenuItemProps,
  EuiContextMenuPanel,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  useEuiTheme,
  EuiSpacer,
  EuiIcon,
} from "@elastic/eui";
import { ShieldIcon, ElipseIcon } from "components/Icons";
import {
  DefaultPageSizeOptions,
  ExportHiddenClass,
  MIN_DASHBOARD_ROW_HEIGHT,
  DEFAULT_NUMBER_OF_ROWS,
} from "Consts";

import {
  convertUIDatesToMomentUserTzDates,
  isNullOrUndefined,
  exportFDDataTable,
  generateExcelFileName,
  camelCaseConverter,
  appendUnitToColumnName,
} from "utils";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import { authSelector } from "reduxStore/auth/auth.selectors";
import FDFullScreen from "./../FDFullScreen";
import { FDPanelTitle, FDPanelTitleProps } from "components/Title/FDPanelTitle";
import { FDSubTitle, IPanelSubTitleProps } from "components/Title/FDSubTitle";
import PanelHeader from "./PanelHeader";
import PanelHeaderProps from "./PanelHeaderProps";
import SearchButton from "components/Buttons/SearchButton";
import Popover from "components/Popover/Popover";
import NoData from "components/NoData/NoData";
import SkeletonPlaceholder from "components/SkeletonPlaceholder";

export interface TablePanelProps {
  accordionInitialIsOpen?: boolean;
  children: React.ReactNode;
  columns: Array<EuiBasicTableColumn<any>>; //todo: new type with renderExcel and use
  data?: any[];
  endDate?: string;
  exportFileName?: string;
  minHeihgt?: number;
  onExportClick?: (columns: any, data: any, currentUser: any, fileName: string) => void;
  onFullScreenChanged?: (isOpen: boolean) => void;
  onSearch?: (seatrchTerm: string) => void;
  panelHeaderProps?: PanelHeaderProps;
  panelHeaderPropsFullScreen?: Partial<FDPanelTitleProps>;
  //PanelHeaderProps
  panelTitleProps?: Partial<FDPanelTitleProps>;
  panelTitlePropsFullScreen?: Partial<FDPanelTitleProps>;
  rightButtons?: JSX.Element[];
  rightTitle?: ReactNode;
  centerContent?: ReactNode | undefined;
  rightTitleProps?: IPanelSubTitleProps;
  showExportButton?: boolean;
  showFullScreen?: boolean;
  showPanel?: boolean;
  showTitleAsAccordion?: boolean;
  startDate?: string;
  tableRef?: any;
  titleActions?: EuiContextMenuItemProps[];
  noBorder?: boolean;
  loading?: boolean;
  subContent?: any;
  customNoData?: any;
  hasHeader?: boolean;
  onNoDataClick?: any;
}

const useStyles = createUseStyles({
  tablePanel: {
    // display: "contents",
    display: "block",
  },
  fullScreen: {
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "100%",
    zIndex: 1000,
  },
  noBorder: {
    border: "none !important",
  },
});

const TablePanel = React.memo<TablePanelProps>(
  ({
    hasHeader = true,
    accordionInitialIsOpen = true,
    children,
    data = [],
    minHeihgt = MIN_DASHBOARD_ROW_HEIGHT,
    panelHeaderProps = {},
    panelHeaderPropsFullScreen = {},
    panelTitleProps = {},
    panelTitlePropsFullScreen = {},
    rightButtons = [],
    showExportButton = true,
    showFullScreen = true,
    showPanel = true,
    showTitleAsAccordion = false,
    titleActions = [],
    onSearch,
    columns,
    exportFileName,
    onExportClick,
    onFullScreenChanged,
    startDate = "",
    endDate = "",
    rightTitle,
    centerContent,
    rightTitleProps = {},
    noBorder = false,
    loading = false,
    subContent = null,
    customNoData = null,
    onNoDataClick,
  }: any) => {
    const theme = useEuiTheme();
    const classes = useStyles();
    const [isPopoverOpen, setPopover] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);

    const { currentUser } = useSelector(authSelector, shallowEqual);
    const { DatePickerParams } = useSelector(globalFilterSelector, shallowEqual);
    const isSilencingWindow = panelTitleProps?.title === "Silencing Window";

    const renderToolsLeft = () => {
      const tools: JSX.Element[] = [];

      if (
        (!panelTitleProps?.isHidden && panelTitleProps?.title) ||
        (fullScreen && panelTitlePropsFullScreen.title)
      ) {
        const titleComponent = panelTitleProps?.iconColor ? (
          <div>
            <FDPanelTitle
              title={""} //should be overriden
              {...panelTitleProps}
              {...(fullScreen ? { ...panelTitlePropsFullScreen } : {})}
              {...(fullScreen ? { titleSize: "s" } : {})}
              style={{ fontSize: "var(--fs16)", fontWeight: "500" }}
            />
          </div>
        ) : (
          <FDPanelTitle
            title={""} //should be overriden
            {...panelTitleProps}
            {...(fullScreen ? { ...panelTitlePropsFullScreen } : {})}
            {...(fullScreen ? { titleSize: "s" } : {})}
            style={{ fontSize: "var(--fs16)", fontWeight: "500" }}
          />
        );
        tools.push(titleComponent);
      }

      return tools;
    };

    const renderActions = () => {
      const tools: JSX.Element[] = [];
      return tools;
    };

    const renderToolsRight = () => {
      const tools: JSX.Element[] = [];
      if (typeof onSearch === "function") {
        tools.push(
          <>
            <SearchButton onSearch={onSearch} />
          </>
        );
      }
      if (rightButtons.length > 0) {
        tools.push(...rightButtons);
      }
      if (rightTitle) {
        //@ts-ignore
        tools.push(<FDSubTitle {...rightTitleProps}>{rightTitle}</FDSubTitle>);
      }
      return tools;
    };

    const leftHeaderItems = renderToolsLeft();
    const rightHeaderItems = renderToolsRight();
    const headerActions = renderActions();

    const closeContextMenu = () => {
      setPopover(false);
      return false;
    };

    const toggleContextMenu = (e: any) => {
      setPopover((v) => !v);
      e.stopPropagation();
    };

    const renderHeader =
      (leftHeaderItems?.length > 0 ||
        rightHeaderItems?.length > 0 ||
        headerActions?.length > 0 ||
        titleActions?.length > 0) &&
      hasHeader;

    const headerActionMenuItems =
      titleActions?.length > 0
        ? [
            ...titleActions?.map((action: any) => {
              return (
                <EuiContextMenuItem
                  autoFocus={false}
                  className="no-focus-list-item"
                  disabled={!data || data.length == 0}
                  icon={action.icon}
                  key={action.key}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick(e);
                    closeContextMenu();
                  }}
                  size="s"
                  {...(action.buttonProps || {})}>
                  {action?.name && action.name}
                </EuiContextMenuItem>
              );
            }),
          ]
        : [];

    const hasData = data && data.length > 0;

    if (renderHeader) {
      if (showExportButton && hasData) {
        headerActionMenuItems?.push(
          <EuiContextMenuItem
            autoFocus={false}
            className="no-focus-list-item"
            disabled={!hasData}
            icon={"download"}
            key="export"
            onClick={(e) => {
              closeContextMenu();
              e.stopPropagation();
              let dates: { endDate: string; startDate: string };

              if (startDate && endDate) {
                dates = {
                  startDate: startDate,
                  endDate: endDate,
                };
              } else {
                const tzDates = convertUIDatesToMomentUserTzDates(
                  DatePickerParams.start,
                  DatePickerParams.end,
                  false
                );
                dates = {
                  startDate: tzDates.start.utc().format("YYYY-MM-DD-HH-mm"),
                  endDate: tzDates.end.utc().format("YYYY-MM-DD-HH-mm"),
                };
              }

              const columnsWithRender = columns.map((c: any) => {
                const col: any = columns.find(
                  (col: any) => col.field === c.field || col.field === c.id
                );
                return {
                  render: c.render,
                  renderExcel: c.renderExcel,
                  ...c,
                };
              });

              const fileName = exportFileName
                ? exportFileName
                : generateExcelFileName(
                    fullScreen
                      ? (panelTitlePropsFullScreen.title ?? panelTitleProps?.title)
                      : panelTitleProps?.title
                  );

              if (onExportClick) {
                //@ts-ignore
                onExportClick(columnsWithRender, data, currentUser, fileName); //in use??
              } else {
                exportFDDataTable(
                  [
                    {
                      columns: appendUnitToColumnName(columnsWithRender),
                      data: data,
                    },
                  ],
                  currentUser,
                  fileName
                );
              }
            }}
            size="s">
            Export
          </EuiContextMenuItem>
        );
      }

      if (showFullScreen && !fullScreen && hasData) {
        headerActionMenuItems?.push(
          <EuiContextMenuItem
            autoFocus={false}
            className="no-focus-list-item"
            disabled={!hasData}
            icon={"fullScreen"}
            key="fullScreen"
            onClick={(e) => {
              e.stopPropagation();
              closeContextMenu();

              if (onFullScreenChanged) {
                //managed outside
                onFullScreenChanged(true);
              } else {
                setFullScreen(true);
              }
            }}
            size="s">
            Full Screen
          </EuiContextMenuItem>
        );
      }
    }
    const topRowContent = (
      <>
        <EuiFlexGroup
          alignItems="center"
          gutterSize="none"
          responsive={false}
          style={{ position: "relative" }}>
          <EuiFlexItem>
            <EuiFlexGroup
              alignItems="center"
              direction="row"
              gutterSize="s"
              responsive={false}
              wrap>
              {leftHeaderItems.map((ctrl, index) => {
                return (
                  <EuiFlexItem grow={1} key={`table_tools_left_${index}`}>
                    {ctrl}
                  </EuiFlexItem>
                );
              })}

              {/* {rightHeaderItems.length > 0 && <EuiFlexItem>&nbsp;</EuiFlexItem>} */}

              {rightHeaderItems.map((ctrl, index) => {
                return (
                  <EuiFlexItem
                    className={ExportHiddenClass}
                    grow={false}
                    key={`table_tools_right_${index}`}>
                    {ctrl}
                  </EuiFlexItem>
                );
              })}
            </EuiFlexGroup>
          </EuiFlexItem>
          {!isNullOrUndefined(centerContent) && (
            <EuiFlexItem style={{ position: "absolute", left: "40.37%" }}>
              {centerContent}
            </EuiFlexItem>
          )}
          {headerActionMenuItems?.length > 0 && (
            <EuiFlexItem className={ExportHiddenClass} grow={false} style={{ paddingLeft: "12px" }}>
              <Popover
                button={
                  <div onClick={toggleContextMenu} style={{ paddingLeft: "4px" }}>
                    <ElipseIcon />
                  </div>
                }
                onClose={closeContextMenu}
                isOpen={isPopoverOpen}
                panelPaddingSize="none"
                children={<EuiContextMenuPanel items={headerActionMenuItems} size="s" />}
              />
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </>
    );

    const header = (
      <>
        {(!showTitleAsAccordion || fullScreen) && (
          <PanelHeader {...panelHeaderProps} {...(fullScreen ? panelHeaderPropsFullScreen : {})}>
            {topRowContent}
          </PanelHeader>
        )}

        {showTitleAsAccordion && !fullScreen && topRowContent}
      </>
    );

    const closeFullScreen = () => {
      if (onFullScreenChanged) {
        //managed outside
        onFullScreenChanged(false);
      } else {
        setFullScreen(false);
      }
    };

    let fullScreenChildrenWithModifiedProps = [];
    if (fullScreen) {
      fullScreenChildrenWithModifiedProps = React.Children.map(children, (child: any) => {
        if (
          Array.isArray(child?.props?.columns) &&
          Array.isArray(child?.props?.items) &&
          !child?.props?.pagination?.totalItemCount
        ) {
          return React.cloneElement(child, {
            pagination: {
              pageSizeOptions: DefaultPageSizeOptions,
              showPerPageOptions: true,
              initialPageSize: DEFAULT_NUMBER_OF_ROWS,
            },
          });
        }
        return child;
      });
    }

    const result = (
      <div
        style={{
          position: "relative",
        }}>
        {!showTitleAsAccordion && renderHeader && header}

        {showTitleAsAccordion && !fullScreen && renderHeader && (
          <EuiAccordion
            buttonContent={header}
            className="card-background properties-accordion"
            id={""}
            initialIsOpen={accordionInitialIsOpen}
            paddingSize="s">
            {loading ? <></> : children}
          </EuiAccordion>
        )}

        {showTitleAsAccordion && fullScreen && renderHeader && header}
        {showTitleAsAccordion && fullScreen && renderHeader && fullScreenChildrenWithModifiedProps}
        {subContent && <div style={{ marginBottom: "32px" }}>{subContent}</div>}
        {(!showTitleAsAccordion || !renderHeader) &&
          fullScreen &&
          fullScreenChildrenWithModifiedProps}

        {(!showTitleAsAccordion || !renderHeader) &&
          !fullScreen &&
          data?.length > 0 &&
          !loading &&
          children}

        {!loading &&
          !showTitleAsAccordion &&
          data?.length === 0 &&
          !customNoData &&
          !["Top Alerts", "Top Resources", "Alerts"].includes(panelTitleProps?.title) && (
            <div
              style={{
                width: "calc(100% + 32px)",
                marginLeft: "-16px",
                marginTop: "-18px",
                borderRadius: "0 0 4px 4px",
                zIndex: "1",
              }}>
              <NoData
                loading={loading}
                noData={isSilencingWindow ? 9999 : 0}
                height={"auto"}
                type="table"
                onClick={onNoDataClick}
              />
            </div>
          )}

        {!loading && !showTitleAsAccordion && data?.length === 0 && customNoData && customNoData}

        {loading && (
          <>
            <EuiSpacer size="m" />
            <EuiFlexGroup
              alignItems="center"
              gutterSize="m"
              responsive={false}
              style={{ margin: "10px" }}>
              {columns.map((c: any) => {
                return (
                  <EuiFlexItem>
                    <SkeletonPlaceholder isLoading lines={3} />
                  </EuiFlexItem>
                );
              })}
            </EuiFlexGroup>
          </>
        )}
      </div>
    );

    return (
      <div
        className={classes.tablePanel + " tablePanel"}
        data-testid={
          camelCaseConverter(panelTitleProps?.title) ||
          camelCaseConverter(panelTitlePropsFullScreen?.title) ||
          "tablePanel"
        }>
        {showPanel && (
          <EuiPanel
            className={noBorder ? "box " + classes.noBorder : "box"}
            hasShadow
            paddingSize="none"
            css={{
              marginBottom: "0px",
            }}
            style={{
              minHeight: panelTitleProps?.title === "Instance Properties" ? 0 : minHeihgt,
              border: "1px solid var(--border-color)",
              borderRadius: "6px",
            }}>
            {result}
          </EuiPanel>
        )}

        {!fullScreen && !showPanel && <>{result}</>}

        {fullScreen && (
          <>
            <FDFullScreen onClose={closeFullScreen}>{result}</FDFullScreen>
          </>
        )}
      </div>
    );
  }
);

export default TablePanel;
